import { xml2js, js2xml } from 'xml-js';
import set from 'lodash.set';
import Pluralize from 'pluralize';

/**
 * @typedef {Object} ParsingOptions
 *  @property {function(node)} filter Returns false to exclude a node. Default is true.
 */

/**
 * Parse the given XML string into an object.
 *
 * @param {String} xml
 * @param {ParsingOptions} [options]
 * @return {Object}
 * @api public
 */
function parse(xml, options = {}) {

    options.filter = options.filter || (() => true);

    function nextChild() {
        return tag() || content() || comment() || cdata();
    }

    function nextRootChild() {
        match(/\s*/);
        return tag(true) || comment() || doctype() || processingInstruction(false);
    }

    function document() {
        const decl = declaration();
        const children = [];
        let documentRootNode;
        let child = nextRootChild();

        while (child) {
            if (child.node.type === 'Element') {
                if (documentRootNode) {
                    throw new Error('Found multiple root nodes');
                }
                documentRootNode = child.node;
            }

            if (!child.excluded) {
                children.push(child.node);
            }

            child = nextRootChild();
        }

        if (!documentRootNode) {
            throw new Error('Failed to parse XML');
        }

        return {
            declaration: decl ? decl.node : null,
            root: documentRootNode,
            children
        };
    }

    function declaration() {
        return processingInstruction(true);
    }

    function processingInstruction(matchDeclaration) {
        const m = matchDeclaration ? match(/^<\?(xml)\s*/) : match(/^<\?([\w-:.]+)\s*/);
        if (!m) return;

        // tag
        const node = {
            name: m[1],
            type: 'ProcessingInstruction',
            attributes: {}
        };

        // attributes
        while (!(eos() || is('?>'))) {
            const attr = attribute();
            if (!attr) return node;
            node.attributes[attr.name] = attr.value;
        }

        match(/\?>/);

        return {
            excluded: matchDeclaration ? false : options.filter(node) === false,
            node
        };
    }

    function tag(matchRoot) {
        const m = match(/^<([\w-:.]+)\s*/);
        if (!m) return;

        // name
        const node = {
            type: 'Element',
            name: m[1],
            attributes: {},
            children: []
        };

        // attributes
        while (!(eos() || is('>') || is('?>') || is('/>'))) {
            const attr = attribute();
            if (!attr) return node;
            node.attributes[attr.name] = attr.value;
        }

        const excluded = matchRoot ? false : options.filter(node) === false;

        // self closing tag
        if (match(/^\s*\/>/)) {
            node.children = null;
            return {
                excluded,
                node
            };
        }

        match(/\??>/);

        if (!excluded) {
            // children
            let child = nextChild();
            while (child) {
                if (!child.excluded) {
                    node.children.push(child.node);
                }
                child = nextChild();
            }
        }

        // closing
        match(/^<\/[\w-:.]+>/);

        return {
            excluded,
            node
        };
    }

    function doctype() {
        const m = match(/^<!DOCTYPE\s+[^>]*>/);
        if (m) {
            const node = {
                type: 'DocumentType',
                content: m[0]
            };
            return {
                excluded: options.filter(node) === false,
                node
            };
        }
    }

    function cdata() {
        if (xml.startsWith('<![CDATA[')) {
            const endPositionStart = xml.indexOf(']]>');
            if (endPositionStart > -1) {
                const endPositionFinish  = endPositionStart + 3;
                const node = {
                    type: 'CDATA',
                    content: xml.substring(0, endPositionFinish)
                };
                xml = xml.slice(endPositionFinish);
                return {
                    excluded: options.filter(node) === false,
                    node
                };
            }
        }
    }

    function comment() {
        const m = match(/^<!--[\s\S]*?-->/);
        if (m) {
            const node = {
                type: 'Comment',
                content: m[0]
            };
            return {
                excluded: options.filter(node) === false,
                node
            };
        }
    }

    function content() {
        const m = match(/^([^<]+)/);
        if (m) {
            const node = {
                type: 'Text',
                content: m[1]
            };
            return {
                excluded: options.filter(node) === false,
                node
            };
        }
    }

    function attribute() {
        const m = match(/([\w:-]+)\s*=\s*("[^"]*"|'[^']*'|\w+)\s*/);
        if (!m) return;
        return {name: m[1], value: strip(m[2])}
    }

    /**
     * Strip quotes from `val`.
     */
    function strip(val) {
        return val.replace(/^['"]|['"]$/g, '');
    }

    /**
     * Match `re` and advance the string.
     */
    function match(re) {
        const m = xml.match(re);
        if (!m) return;
        xml = xml.slice(m[0].length);
        return m;
    }

    /**
     * End-of-source.
     */
    function eos() {
        return 0 === xml.length;
    }

    /**
     * Check for `prefix`.
     */
    function is(prefix) {
        return 0 === xml.indexOf(prefix);
    }

    xml = xml.trim();

    return document();
}

var xmlParserXo = parse;

/**
 * @typedef {Object} XMLFormatterOptions
 *  @property {String} [indentation='    '] The value used for indentation
 *  @property {function(node): boolean} [filter] Return false to exclude the node.
 *  @property {Boolean} [collapseContent=false] True to keep content in the same line as the element. Only works if element contains at least one text node
 *  @property {String} [lineSeparator='\r\n'] The line separator to use
 *  @property {String} [whiteSpaceAtEndOfSelfclosingTag=false] to either end ad self closing tag with `<tag/>` or `<tag />`
 */

/**
 * @typedef {Object} XMLFormatterState
 * @param {String} content
 * @param {Number} level
 * @param {XMLFormatterOptions} options
 */

/**
 * @param {XMLFormatterState} state
 * @return {void}
 */
function newLine(state) {
    if (!state.options.indentation && !state.options.lineSeparator) return;
    state.content += state.options.lineSeparator;
    let i;
    for (i = 0; i < state.level; i++) {
        state.content += state.options.indentation;
    }
}

/**
 * @param {XMLFormatterState} state
 * @param {String} content
 * @return {void}
 */
function appendContent(state, content) {
    state.content += content;
}

/**
 * @param {Object} node
 * @param {XMLFormatterState} state
 * @param {Boolean} preserveSpace
 * @return {void}
 */
function processNode(node, state, preserveSpace) {
    if (typeof node.content === 'string') {
        processContentNode(node, state, preserveSpace);
    } else if (node.type === 'Element') {
        processElementNode(node, state, preserveSpace);
    } else if (node.type === 'ProcessingInstruction') {
        processProcessingIntruction(node, state);
    } else {
        throw new Error('Unknown node type: ' + node.type);
    }
}

/**
 * @param {Object} node
 * @param {XMLFormatterState} state
 * @param {Boolean} preserveSpace
 * @return {void}
 */
function processContentNode(node, state, preserveSpace) {
    if (!preserveSpace) {
        node.content = node.content.trim();
    }
    if (node.content.length > 0) {
        if (!preserveSpace && state.content.length > 0) {
            newLine(state);
        }
        appendContent(state, node.content);
    }
}

/**
 * @param {Object} node
 * @param {XMLFormatterState} state
 * @param {Boolean} preserveSpace
 * @return {void}
 */
function processElementNode(node, state, preserveSpace) {
    if (!preserveSpace && state.content.length > 0) {
        newLine(state);
    }

    appendContent(state, '<' + node.name);
    processAttributes(state, node.attributes);

    if (node.children === null) {
        const selfClosingNodeClosingTag = state.options.whiteSpaceAtEndOfSelfclosingTag ? ' />' : '/>';
        // self-closing node
        appendContent(state, selfClosingNodeClosingTag);
    } else if (node.children.length === 0) {
        // empty node
        appendContent(state, '></' + node.name + '>');
    } else {

        appendContent(state, '>');

        state.level++;

        let nodePreserveSpace = node.attributes['xml:space'] === 'preserve';

        if (!nodePreserveSpace && state.options.collapseContent) {

            const containsTextNodes = node.children.some(function(child) {
                return child.type === 'Text' && child.content.trim() !== '';
            });

            if (containsTextNodes) {
                nodePreserveSpace = true;
            }
        }

        node.children.forEach(function(child) {
            processNode(child, state, preserveSpace || nodePreserveSpace, state.options);
        });

        state.level--;

        if (!preserveSpace && !nodePreserveSpace) {
            newLine(state);
        }
        appendContent(state, '</' + node.name + '>');
    }
}

/**
 * @param {XMLFormatterState} state
 * @param {Record<String, String>} attributes
 * @return {void}
 */
function processAttributes(state, attributes) {
    Object.keys(attributes).forEach(function(attr) {
        const escaped = attributes[attr].replace(/"/g, '&quot;');
        appendContent(state, ' ' + attr + '="' + escaped + '"');
    });
}

/**
 * @param {Object} node
 * @param {XMLFormatterState} state
 * @return {void}
 */
function processProcessingIntruction(node, state) {
    if (state.content.length > 0) {
        newLine(state);
    }
    appendContent(state, '<?' + node.name);
    processAttributes(state, node.attributes);
    appendContent(state, '?>');
}


/**
 * Converts the given XML into human readable format.
 *
 * @param {String} xml
 * @param {XMLFormatterOptions} options
 * @returns {string}
 */
function format(xml, options = {}) {
    options.indentation = 'indentation' in options ? options.indentation : '    ';
    options.collapseContent = options.collapseContent === true;
    options.lineSeparator = 'lineSeparator' in options ? options.lineSeparator : '\r\n';
    options.whiteSpaceAtEndOfSelfclosingTag = !!options.whiteSpaceAtEndOfSelfclosingTag;

    const parser = xmlParserXo;
    const parsedXml = parser(xml, {filter: options.filter});
    const state = {content: '', level: 0, options: options};

    if (parsedXml.declaration) {
        processProcessingIntruction(parsedXml.declaration, state);
    }

    parsedXml.children.forEach(function(child) {
        processNode(child, state, false);
    });

    return state.content;
}


var xmlFormatter = format;

/**
 * Get a random uuid like identifier
 *
 *
 * @returns {string}
 */
var uuid = function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0; // eslint-disable-line

    var v = c === 'x' ? r : r & 0x3 | 0x8; // eslint-disable-line

    return v.toString(16);
  });
};
/**
 * Find the Greatest Common Divisor or GCD of two numbers
 *
 * @param {number} x
 * @param {number} y
 * @returns {number}
 */


var gcd = function gcd(x, y) {
  if (typeof x !== 'number' || typeof y !== 'number') {
    throw new Error('x and y type must be a number');
  }

  var absX = Math.abs(x);
  var absY = Math.abs(y);

  while (absY) {
    var temp = absY;
    absY = absX % absY;
    absX = temp;
  }

  return absX;
};
/**
 * Find the Greatest Common Divisor or GCD for series of numbers
 *
 * @param {Array<number>} numbers
 * @returns {number}
 */


var gcdForNumbers = function gcdForNumbers(numbers) {
  if (!Array.isArray(numbers)) {
    throw new Error('numbers type must ba an Array');
  }

  var count = numbers.length;

  if (!count) {
    return null;
  }

  var a = numbers[0];
  var b;

  for (var i = 1; i < count; i += 1) {
    b = numbers[i];
    a = gcd(a, b);
  }

  return a;
};

var isNumeric = function isNumeric(n) {
  return n !== '' && n !== null && !Array.isArray(n) && !Number.isNaN(+n) && Number.isFinite(+n);
};

/**
 * Create XML element from string or base element object
 *
 * @param {Object|string} elementOrName
 * @returns {Object}
 */
var createElement = function createElement(elementOrName) {
  var baseElement = {
    type: 'element',
    attributes: {},
    $$: []
  };

  if (typeof elementOrName === 'string') {
    return Object.assign({}, baseElement, {
      name: elementOrName
    });
  }

  return Object.assign({
    type: 'element',
    attributes: {},
    $$: []
  }, elementOrName);
};
/**
 * Add child to provided element
 *
 * @param {import("xml-js").Element} element
 * @param {import("xml-js").Element|string} child
 * @param {Object} [{ unique = false }={}]
 * @returns Added element
 */


var addChild = function addChild(element, child, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$unique = _ref.unique,
      unique = _ref$unique === void 0 ? false : _ref$unique,
      _ref$unshift = _ref.unshift,
      unshift = _ref$unshift === void 0 ? false : _ref$unshift;

  if (unique) {
    var existingChild = element.$$.find(function (item) {
      if (item.type === 'text') {
        return false;
      }

      return item.name === child || item.name === child.name;
    });

    if (existingChild) {
      return existingChild;
    }
  }

  var insertElement = function insertElement(newElement) {
    if (unshift) {
      element.$$.unshift(newElement);
    } else {
      element.$$.push(newElement);
    }
  };

  if (typeof child === 'string') {
    var newChild = createElement(child);
    insertElement(newChild);
    return newChild;
  }

  insertElement(child);
  return child;
};
/**
 * Escape xml special characters & " < > '
 *
 * @param {string} value
 * @returns {string}
 */


var xmlEscape = function xmlEscape(value) {
  if (typeof value !== 'string') {
    throw new Error("value of type string expected, got " + typeof value);
  }

  var xmlEscapeMap = {
    '>': '&gt;',
    '<': '&lt;',
    "'": '&apos;',
    '"': '&quot;',
    '&': '&amp;'
  };
  return value.replace(/([&"<>'])/g, function (match, item) {
    return xmlEscapeMap[item];
  });
};
/**
 * Unescape xml special characters &gt; &lt; &quot; &amp; &apos;
 *
 * @param {string} value
 * @returns {string}
 */


var xmlUnescape = function xmlUnescape(value) {
  if (typeof value !== 'string') {
    throw new Error("value of type string expected, got " + typeof value);
  }

  var xmlUnescapeMap = {
    '&gt;': '>',
    '&lt;': '<',
    '&apos;': "'",
    '&quot;': '"',
    '&amp;': '&'
  };
  return value.replace(/(&gt;)|(&lt;)|(&quot;)|(&amp;)|(&apos;)/g, function (match) {
    return xmlUnescapeMap[match];
  });
};
/**
 * Parse children and differentiate presentation and semantics
 * elements from other children.
 *
 * Optionally for performance reasons you can give callback
 * method to handle each child.
 *
 *
 * @param {Object} element
 * @param {string} [namespace='pio']
 * @param {Function} [handleChild=(child, childType) => null]
 * @return {Object} { children. presentationElements, semanticsElements }
 */


var parseChildren = function parseChildren(element, namespace, handleChild) {
  if (namespace === void 0) {
    namespace = 'pio';
  }

  if (handleChild === void 0) {
    handleChild = function handleChild(child, childType) {
      return null;
    };
  }

  var handleChildCallback = function handleChildCallback(acc, child, index) {
    var childType = child.name || child.type;

    if (typeof handleChild === 'function') {
      handleChild(child, childType);
    }

    switch (childType) {
      case namespace + ":presentation":
        if (acc.presentationElements) {
          throw new Error(childType + " element must be unique!");
        }

        acc.presentationElements = child;
        break;

      case namespace + ":semantics":
        if (acc.semanticsElements) {
          throw new Error(childType + " element must be unique!");
        }

        acc.semanticElements = child;
        break;

      default:
        acc.children.push(child);
    }

    return acc;
  };

  return element.$$.reduce(handleChildCallback, {
    children: []
  });
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var isDev = process.env.NODE_ENV !== 'production';
var xmlConverterOptions = {
  compact: false,
  elementsKey: '$$',
  alwaysChildren: true
};
var xml2jsOptions = Object.assign({}, xmlConverterOptions, {
  attributeValueFn: function attributeValueFn(value, name) {
    // escape xmlns namespace attributes
    if (name.startsWith('xmlns')) {
      return undefined;
    }

    return xmlUnescape(value);
  }
});
var js2xmlOptions = Object.assign({}, xmlConverterOptions, {
  attributeValueFn: function attributeValueFn(value) {
    return xmlEscape(value);
  }
});
var prettyFormatOptions = {
  indentation: '\t',
  lineSeparator: '\n',
  collapseContent: false
};

var createXmlConverterService = function createXmlConverterService(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      defaultOptions = _ref.options;

  var instance = {
    options: Object.assign({}, defaultOptions)
  };
  /**
   * Do various cleanups to ensure XML is valid
   *
   * This is mainly because HTML format is more loose
   * then XML.
   *
   * @param {string} xmlString
   * @return {string}
   */

  var cleanup = function cleanup(xmlString) {
    if (typeof xmlString !== 'string') {
      throw new Error("type is invalid, string expected but got '" + typeof xmlString + "'");
    }

    return xmlString.replace(/<\/?br>/g, '<br/>');
  };

  var minify = function minify(xmlString) {
    return xmlString.replace(/\t|\n/g, '');
  };

  var parse = function parse(xmlString, _temp2) {
    var _ref2 = _temp2 === void 0 ? {} : _temp2,
        _ref2$pretty = _ref2.pretty,
        pretty = _ref2$pretty === void 0 ? false : _ref2$pretty;

    var cleanXmlString = cleanup(xmlString);

    if (pretty) {
      return xml2js(minify(cleanXmlString), xml2jsOptions);
    }

    return xml2js(cleanXmlString, xml2jsOptions);
  };

  var prettify = function prettify(xmlString, options) {
    if (options === void 0) {
      options = {};
    }

    var cleanXmlString = cleanup(xmlString);

    if (options.noRoot) {
      return xmlFormatter("<root>" + cleanXmlString + "</root>", Object.assign({}, prettyFormatOptions, options)).replace('<root>', '').replace('</root>', '');
    }

    return xmlFormatter(cleanXmlString, Object.assign({}, prettyFormatOptions, options));
  };

  var format = function format(xmlObject, _temp3) {
    var _ref3 = _temp3 === void 0 ? {} : _temp3,
        _ref3$pretty = _ref3.pretty,
        pretty = _ref3$pretty === void 0 ? false : _ref3$pretty;

    var xmlString = js2xml(xmlObject, js2xmlOptions);

    if (pretty) {
      return prettify(xmlString);
    }

    return xmlString;
  };

  return Object.freeze(Object.assign({}, instance, {
    parse: parse,
    format: format,
    prettify: prettify,
    minify: minify
  }));
};

var XmlConverterService = createXmlConverterService();

if (isDev && process.browser) {
  window.xmlConverter = XmlConverterService;
}

var _excluded = ["class"],
    _excluded2 = ["role"];

var Emphasis = function Emphasis(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      inlineNormalizer = _ref.inlineNormalizer;

  if (!inlineNormalizer) {
    throw new Error('inlineNormalizer is required!');
  }

  var instance = {};
  return Object.freeze(Object.assign({}, instance, {
    normalize: function normalize(element) {
      var _element$$$;

      var _ref2 = element.attributes || {},
          className = _ref2["class"],
          attributes = _objectWithoutPropertiesLoose(_ref2, _excluded);

      return Object.assign({}, element, {
        name: 'emphasis',
        attributes: Object.assign({}, attributes, {
          role: className
        }),
        $$: (_element$$$ = element.$$) === null || _element$$$ === void 0 ? void 0 : _element$$$.map(function (child) {
          return inlineNormalizer.normalize(child);
        })
      });
    },
    denormalize: function denormalize(element) {
      var _element$$$2;

      var _ref3 = element.attributes || {},
          role = _ref3.role,
          attributes = _objectWithoutPropertiesLoose(_ref3, _excluded2);

      return Object.assign({}, element, {
        name: role ? 'span' : 'em',
        attributes: Object.assign({}, attributes, {
          "class": role
        }),
        $$: (_element$$$2 = element.$$) === null || _element$$$2 === void 0 ? void 0 : _element$$$2.map(function (child) {
          return inlineNormalizer.denormalize(child);
        })
      });
    }
  }));
};

var Link = function Link(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      inlineNormalizer = _ref.inlineNormalizer;

  if (!inlineNormalizer) {
    throw new Error('inlineNormalizer is required!');
  }

  var instance = {};
  return Object.freeze(Object.assign({}, instance, {
    normalize: function normalize(element) {
      var _element$$$;

      return Object.assign({}, element, {
        name: 'ulink',
        attributes: {
          url: element.attributes.href
        },
        $$: (_element$$$ = element.$$) === null || _element$$$ === void 0 ? void 0 : _element$$$.map(function (child) {
          return inlineNormalizer.normalize(child);
        })
      });
    },
    denormalize: function denormalize(element) {
      var _element$$$2;

      return Object.assign({}, element, {
        name: 'a',
        attributes: {
          href: element.attributes.url
        },
        $$: (_element$$$2 = element.$$) === null || _element$$$2 === void 0 ? void 0 : _element$$$2.map(function (child) {
          return inlineNormalizer.denormalize(child);
        })
      });
    }
  }));
};

var SPACE_CONSTANT = '___SPACE_CONSTANT___';

var Strikethrough = function Strikethrough(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      inlineNormalizer = _ref.inlineNormalizer;

  if (!inlineNormalizer) {
    throw new Error('inlineNormalizer is required!');
  }

  var instance = {};
  return Object.freeze(Object.assign({}, instance, {
    normalize: function normalize(element) {
      var _element$$$;

      return Object.assign({}, element, {
        name: 'strikethrough',
        attributes: {},
        $$: (_element$$$ = element.$$) === null || _element$$$ === void 0 ? void 0 : _element$$$.map(function (child) {
          return inlineNormalizer.normalize(child);
        })
      });
    },
    denormalize: function denormalize(element) {
      var _element$$$2;

      return Object.assign({}, element, {
        name: 's',
        attributes: {},
        $$: (_element$$$2 = element.$$) === null || _element$$$2 === void 0 ? void 0 : _element$$$2.map(function (child) {
          return inlineNormalizer.denormalize(child);
        })
      });
    }
  }));
};

var Superscript = function Superscript(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      inlineNormalizer = _ref.inlineNormalizer;

  if (!inlineNormalizer) {
    throw new Error('inlineNormalizer is required!');
  }

  var instance = {};
  return Object.freeze(Object.assign({}, instance, {
    normalize: function normalize(element) {
      var _element$$$;

      return Object.assign({}, element, {
        name: 'superscript',
        attributes: {},
        $$: (_element$$$ = element.$$) === null || _element$$$ === void 0 ? void 0 : _element$$$.map(function (child) {
          return inlineNormalizer.normalize(child);
        })
      });
    },
    denormalize: function denormalize(element) {
      var _element$$$2;

      return Object.assign({}, element, {
        name: 'sup',
        attributes: {},
        $$: (_element$$$2 = element.$$) === null || _element$$$2 === void 0 ? void 0 : _element$$$2.map(function (child) {
          return inlineNormalizer.denormalize(child);
        })
      });
    }
  }));
};

var Subscript = function Subscript(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      inlineNormalizer = _ref.inlineNormalizer;

  if (!inlineNormalizer) {
    throw new Error('inlineNormalizer is required!');
  }

  var instance = {};
  return Object.freeze(Object.assign({}, instance, {
    normalize: function normalize(element) {
      var _element$$$;

      return Object.assign({}, element, {
        name: 'subscript',
        attributes: {},
        $$: (_element$$$ = element.$$) === null || _element$$$ === void 0 ? void 0 : _element$$$.map(function (child) {
          return inlineNormalizer.normalize(child);
        })
      });
    },
    denormalize: function denormalize(element) {
      var _element$$$2;

      return Object.assign({}, element, {
        name: 'sub',
        attributes: {},
        $$: (_element$$$2 = element.$$) === null || _element$$$2 === void 0 ? void 0 : _element$$$2.map(function (child) {
          return inlineNormalizer.denormalize(child);
        })
      });
    }
  }));
};

var LineBreak = function LineBreak(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      inlineNormalizer = _ref.inlineNormalizer;

  if (!inlineNormalizer) {
    throw new Error('inlineNormalizer is required!');
  }

  var instance = {};
  return Object.freeze(Object.assign({}, instance, {
    normalize: function normalize(element) {
      return Object.assign({}, element, {
        name: inlineNormalizer.namespace + ":linebreak",
        attributes: {},
        $$: []
      });
    },
    denormalize: function denormalize(element) {
      return Object.assign({}, element, {
        name: 'br',
        attributes: {},
        $$: []
      });
    }
  }));
};

var User = function User(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      inlineNormalizer = _ref.inlineNormalizer;

  if (!inlineNormalizer) {
    throw new Error('inlineNormalizer is required!');
  }

  var instance = {};
  return Object.freeze(Object.assign({}, instance, {
    normalize: function normalize(element) {
      if (element.attributes['data-entity'] === 'users') {
        return Object.assign({}, element, {
          name: 'pio:user',
          attributes: {
            id: element.attributes['data-id']
          },
          $$: []
        });
      }

      return element;
    },
    denormalize: function denormalize(element) {
      var _element$attributes, _element$attributes2, _element$attributes2$;

      return Object.assign({}, element, {
        name: 'cite',
        attributes: {
          'data-id': element === null || element === void 0 ? void 0 : (_element$attributes = element.attributes) === null || _element$attributes === void 0 ? void 0 : _element$attributes.id,
          'data-entity': 'users'
        },
        $$: [{
          type: 'text',
          text: "Korisnik #" + (element === null || element === void 0 ? void 0 : (_element$attributes2 = element.attributes) === null || _element$attributes2 === void 0 ? void 0 : (_element$attributes2$ = _element$attributes2.id) === null || _element$attributes2$ === void 0 ? void 0 : _element$attributes2$.substring(0, 6))
        }]
      });
    }
  }));
};

var _excluded$1 = ["name", "attributes"],
    _excluded2$1 = ["class"];
var spaceConstantMatch = new RegExp("" + SPACE_CONSTANT, 'g');

var InlineNormalizer = function InlineNormalizer(_ref) {
  var blocks2Docbook = _ref.blocks2Docbook,
      defaultOptions = _ref.options;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    namespace: blocks2Docbook.namespace,
    options: Object.assign({}, defaultOptions)
  };

  instance.normalize = function (element) {
    var _element$text, _element$$$;

    switch (element.name || element.type) {
      case 'text':
        return Object.assign({}, element, {
          text: (_element$text = element.text) === null || _element$text === void 0 ? void 0 : _element$text.replace(spaceConstantMatch, ' ')
        });

      case 'span':
      case 'em':
        return Emphasis({
          inlineNormalizer: instance
        }).normalize(element);

      case 'a':
        return Link({
          inlineNormalizer: instance
        }).normalize(element);

      case 's':
        return Strikethrough({
          inlineNormalizer: instance
        }).normalize(element);

      case 'sup':
        return Superscript({
          inlineNormalizer: instance
        }).normalize(element);

      case 'sub':
        return Subscript({
          inlineNormalizer: instance
        }).normalize(element);

      case 'br':
        return LineBreak({
          inlineNormalizer: instance
        }).normalize(element);

      case 'strong':
      case 'code':
        return Object.assign({}, element, {
          $$: ((_element$$$ = element.$$) === null || _element$$$ === void 0 ? void 0 : _element$$$.map(function (child) {
            return instance.normalize(child);
          })) || []
        });

      case 'cite':
        return User({
          inlineNormalizer: instance
        }).normalize(element);

      default:
        {
          var _element$$$2;

          // all other (unsupported) elements are stripped and
          // converted to emphasis elements with role that has
          // original element name in role attribute
          var name = element.name,
              attributes = element.attributes,
              restElement = _objectWithoutPropertiesLoose(element, _excluded$1);

          var _ref2 = attributes || {},
              className = _ref2["class"],
              restAttributes = _objectWithoutPropertiesLoose(_ref2, _excluded2$1);

          return Object.assign({}, restElement, {
            name: 'emphasis',
            attributes: Object.assign({}, restAttributes, {
              role: ["element-name-" + name, className].filter(Boolean).join(' ')
            }),
            $$: ((_element$$$2 = element.$$) === null || _element$$$2 === void 0 ? void 0 : _element$$$2.map(function (child) {
              return instance.normalize(child);
            })) || []
          });
        }
    }
  };

  instance.denormalize = function (element) {
    var _element$text2, _element$$$3;

    switch (element.name || element.type) {
      case 'text':
        return Object.assign({}, element, {
          text: (_element$text2 = element.text) === null || _element$text2 === void 0 ? void 0 : _element$text2.replace(spaceConstantMatch, ' ')
        });

      case 'emphasis':
        return Emphasis({
          inlineNormalizer: instance
        }).denormalize(element);

      case 'ulink':
        return Link({
          inlineNormalizer: instance
        }).denormalize(element);

      case 'strikethrough':
        return Strikethrough({
          inlineNormalizer: instance
        }).denormalize(element);

      case 'superscript':
        return Superscript({
          inlineNormalizer: instance
        }).denormalize(element);

      case 'subscript':
        return Subscript({
          inlineNormalizer: instance
        }).denormalize(element);

      case instance.namespace + ":linebreak":
        return LineBreak({
          inlineNormalizer: instance
        }).denormalize(element);

      case instance.namespace + ":user":
        return User({
          inlineNormalizer: instance
        }).denormalize(element);

      default:
        return Object.assign({}, element, {
          $$: ((_element$$$3 = element.$$) === null || _element$$$3 === void 0 ? void 0 : _element$$$3.map(function (child) {
            return instance.denormalize(child);
          })) || []
        });
    }
  };

  return Object.freeze({
    namespace: instance.namespace,
    options: instance.options,
    normalize: instance.normalize,
    denormalize: instance.denormalize
  });
};

var Paragraph = function Paragraph(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var block = {
        name: 'core/paragraph',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {}
      };

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        } else {
          acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements,
          children = _element$$$$reduce.children;

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          var _presentationElement$, _presentationElement$2;

          switch (presentationElement.name) {
            case 'typography':
              block.attributes.dropCap = ((_presentationElement$ = presentationElement.attributes) === null || _presentationElement$ === void 0 ? void 0 : _presentationElement$['drop-cap']) === 'true';
              break;

            case 'alignment':
              block.attributes.align = (_presentationElement$2 = presentationElement.attributes) === null || _presentationElement$2 === void 0 ? void 0 : _presentationElement$2.horizontal;
              break;

            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      if (children !== null && children !== void 0 && children.some(function (child) {
        return child.type !== 'text';
      })) {
        var xmlString = blocks2Docbook.xmlConverter.format({
          $$: [{
            name: 'para',
            type: 'element',
            $$: children.map(function (child) {
              return instance.normalizer.denormalize(child);
            })
          }]
        });
        block.attributes.content = xmlString.replace(/^<para>/, '').replace(/<\/para>$/, '');
      } else {
        var _children$find;

        block.attributes.content = ((_children$find = children.find(function (child) {
          return child.type === 'text';
        })) === null || _children$find === void 0 ? void 0 : _children$find.text) || '';
      }

      return block;
    },
    format: function format(block) {
      var attributes = block.attributes;
      var element = {
        name: 'para',
        type: 'element',
        attributes: {},
        $$: []
      };
      var presentationAttributes = Object.keys(attributes);
      var presentation;
      presentationAttributes.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'content':
            try {
              if (attributes.content) {
                var xmlObject = blocks2Docbook.xmlConverter.parse("<para>" + attributes.content.replace(/> </g, ">" + SPACE_CONSTANT + "<") + "</para>");

                if (xmlObject.$$.length > 0) {
                  xmlObject.$$[0].$$.forEach(function (child) {
                    addChild(element, instance.normalizer.normalize(child));
                  });
                }
              }
            } catch (error) {
              if (!error.message.includes('XML parsing error')) {
                // eslint-disable-next-line no-console
                console.error(error);
              }

              addChild(element, {
                type: 'text',
                text: attributes.content
              });
            }

            break;

          case 'dropCap':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'typography', {
              unique: true
            }), 'attributes.drop-cap', attributes.dropCap);
            break;

          case 'align':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'alignment', {
              unique: true
            }), 'attributes.horizontal', attributes.align);
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      return element;
    }
  }));
};

var _excluded$2 = ["ordered", "anchor"];

var List = function List(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };

  var parseListItem = function parseListItem(element) {
    var listItem = Object.assign({}, element, {
      name: 'li',
      $$: []
    });
    element.$$.forEach(function (child) {
      if (['itemizedlist', 'orderedlist'].includes(child.name)) {
        var orderedNested = child.name === 'orderedlist';
        var list = addChild(listItem, orderedNested ? 'ol' : 'ul', {
          unique: true
        });
        child.$$.forEach(function (nestedChild) {
          var nestedListItem = parseListItem(nestedChild);
          list.$$.push(nestedListItem);
        });
      } else {
        child.$$.forEach(function (nestedChild) {
          listItem.$$.push(instance.normalizer.denormalize(nestedChild));
        });
      }
    });
    return listItem;
  };

  var formatListItem = function formatListItem(element) {
    var listItem = Object.assign({}, element, {
      name: 'listitem',
      $$: []
    });

    if (element.$$.length === 0) {
      // insert placeholder content for empty list items
      addChild(listItem, 'para', {
        unique: true,
        unshift: true
      });
    } else {
      element.$$.forEach(function (child) {
        if (['ul', 'ol'].includes(child.name)) {
          var orderedNested = child.name === 'ol';
          var list = addChild(listItem, orderedNested ? 'orderedlist' : 'itemizedlist', {
            unique: true
          });
          child.$$.forEach(function (nestedChild) {
            var nestedListItem = formatListItem(nestedChild);
            list.$$.push(nestedListItem);
          });
        } else {
          var para = addChild(listItem, 'para', {
            unique: true,
            unshift: true
          });
          para.$$.push(instance.normalizer.normalize(child));
        }
      });
    }

    return listItem;
  };

  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var block = {
        name: 'core/list',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {
          ordered: element.name === 'orderedlist'
        }
      };

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        } else {
          acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements,
          children = _element$$$$reduce.children;

      if (typeof (presentationElements === null || presentationElements === void 0 ? void 0 : presentationElements.attributes) === 'object') {
        Object.keys(presentationElements.attributes).forEach(function (attribute) {
          var _presentationElements, _presentationElements2;

          switch (attribute) {
            case 'start':
              block.attributes.start = +((_presentationElements = presentationElements.attributes) === null || _presentationElements === void 0 ? void 0 : _presentationElements.start) || undefined;
              break;

            case 'reversed':
              block.attributes.reversed = ((_presentationElements2 = presentationElements.attributes) === null || _presentationElements2 === void 0 ? void 0 : _presentationElements2.reversed) === 'true';
              break;

            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElements.name + "' is not supported!"));
          }
        });
      }

      block.attributes.values = children.reduce(function (acc, child) {
        return acc + blocks2Docbook.xmlConverter.format({
          $$: [parseListItem(child)]
        });
      }, '');
      return block;
    },
    format: function format(block) {
      var blockAttributes = block.attributes;

      var ordered = blockAttributes.ordered,
          anchor = blockAttributes.anchor,
          attributes = _objectWithoutPropertiesLoose(blockAttributes, _excluded$2);

      var element = {
        name: ordered ? 'orderedlist' : 'itemizedlist',
        type: 'element',
        attributes: {},
        $$: []
      };
      var presentationAttributes = Object.keys(attributes);
      var presentation;
      presentationAttributes.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'values':
            {
              var xmlObject = blocks2Docbook.xmlConverter.parse("<list>" + attributes[attribute].replace(/> </g, ">" + SPACE_CONSTANT + "<") + "</list>");

              if (xmlObject.$$.length > 0) {
                xmlObject.$$[0].$$.forEach(function (child) {
                  var listItem = formatListItem(child);
                  addChild(element, listItem);
                });
              }

              break;
            }

          case 'start':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(presentation, 'attributes.start', attributes.start);
            break;

          case 'reversed':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(presentation, 'attributes.reversed', attributes.reversed);
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      return element;
    }
  }));
};

var _excluded$3 = ["content"],
    _excluded2$2 = ["head", "body", "foot"];

var Table = function Table(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };

  var parseRows = function parseRows(rows, name) {
    if (name === void 0) {
      name = 'td';
    }

    if (Array.isArray(rows) && rows.length > 0) {
      return rows.map(function (row) {
        var rowElement = {
          cells: []
        };

        if (Array.isArray(row.$$)) {
          rowElement.cells = row.$$.map(function (entry) {
            var cell = {
              tag: name
            };

            var _entry$$$$reduce = entry.$$.reduce(function (acc, child) {
              if (child.name === blocks2Docbook.namespace + ":presentation") {
                if (acc.presentationElements) {
                  throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
                }

                acc.presentationElements = child;
              } else {
                acc.children.push(child);
              }

              return acc;
            }, {
              children: []
            }),
                presentationElements = _entry$$$$reduce.presentationElements,
                children = _entry$$$$reduce.children;

            if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
              presentationElements.$$.forEach(function (presentationElement) {
                var _presentationElement$;

                switch (presentationElement.name) {
                  case 'alignment':
                    cell.align = (_presentationElement$ = presentationElement.attributes) === null || _presentationElement$ === void 0 ? void 0 : _presentationElement$.horizontal;
                    break;

                  default:
                    // eslint-disable-next-line no-console
                    console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
                }
              });
            }

            cell.content = blocks2Docbook.xmlConverter.format({
              $$: children.map(function (child) {
                return instance.normalizer.denormalize(child);
              })
            });
            return cell;
          });
        }

        return rowElement;
      });
    }

    return [];
  };

  var formatRows = function formatRows(element, rows, name) {
    if (name === void 0) {
      name = 'tbody';
    }

    if (Array.isArray(rows) && rows.length > 0) {
      var headElement = addChild(element, name, {
        unique: true
      });
      rows.forEach(function (row) {
        var rowElement = addChild(headElement, 'row');

        if (Array.isArray(row.cells)) {
          row.cells.forEach(function (cell) {
            var content = cell.content,
                attributes = _objectWithoutPropertiesLoose(cell, _excluded$3);

            var xmlObject = blocks2Docbook.xmlConverter.parse("<para>" + content.replace(/> </g, ">" + SPACE_CONSTANT + "<") + "</para>");
            var entryElement = addChild(rowElement, {
              name: 'entry',
              type: 'element',
              attributes: {},
              $$: xmlObject.$$[0].$$.map(function (child) {
                return instance.normalizer.normalize(child);
              })
            });
            var presentationAttributes = Object.keys(cell);
            var presentation;
            presentationAttributes.forEach(function (attribute) {
              if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
                return;
              }

              switch (attribute) {
                case 'tag':
                  break;

                case 'align':
                  presentation = addChild(entryElement, blocks2Docbook.namespace + ":presentation", {
                    unique: true
                  });
                  set(addChild(presentation, 'alignment', {
                    unique: true
                  }), 'attributes.horizontal', attributes.align);
                  break;

                default:
                  // eslint-disable-next-line no-console
                  console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
              }
            });
          });
        }
      });
    }
  };

  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var block = {
        name: 'core/table',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {
          head: [],
          body: [],
          foot: [],
          hasFixedLayout: false,
          caption: ''
        }
      };

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        } else {
          acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements,
          children = _element$$$$reduce.children;

      Object.keys(element.attributes || {}).forEach(function (attribute) {
        if (typeof element.attributes[attribute] === 'undefined' || element.attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'role':
            block.attributes.className = element.attributes.role;
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          var _presentationElement$2;

          switch (presentationElement.name) {
            case 'alignment':
              block.attributes.align = (_presentationElement$2 = presentationElement.attributes) === null || _presentationElement$2 === void 0 ? void 0 : _presentationElement$2.horizontal;
              break;

            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      children.forEach(function (child) {
        switch (child.name) {
          case 'title':
            block.attributes.caption = blocks2Docbook.xmlConverter.format(Object.assign({}, child, {
              $$: child.$$.map(function (inlineChild) {
                return instance.normalizer.denormalize(inlineChild);
              })
            }));
            break;

          case 'tgroup':
            child.$$.forEach(function (groupChild) {
              switch (groupChild.name) {
                case 'thead':
                  block.attributes.head = parseRows(groupChild.$$, 'th');
                  break;

                case 'tbody':
                  block.attributes.body = parseRows(groupChild.$$, 'td');
                  break;

                case 'tfoot':
                  block.attributes.foot = parseRows(groupChild.$$, 'tfoot');
                  break;

                default:
                  throw new Error("Element '" + groupChild.name + "' is not supported!");
              }
            });
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Element '" + child.name + "' is not supported!"));
        }
      });
      return block;
    },
    format: function format(block) {
      var _head$, _head$$cells, _foot$, _foot$$cells, _body$, _body$$cells;

      var blockAttributes = block.attributes;

      var _blockAttributes$head = blockAttributes.head,
          head = _blockAttributes$head === void 0 ? [] : _blockAttributes$head,
          _blockAttributes$body = blockAttributes.body,
          body = _blockAttributes$body === void 0 ? [] : _blockAttributes$body,
          _blockAttributes$foot = blockAttributes.foot,
          foot = _blockAttributes$foot === void 0 ? [] : _blockAttributes$foot,
          attributes = _objectWithoutPropertiesLoose(blockAttributes, _excluded2$2);

      var element = {
        name: 'table',
        type: 'element',
        attributes: {},
        $$: []
      };
      var presentationAttributes = Object.keys(attributes);
      var presentation;
      presentationAttributes.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'caption':
            {
              var titleElement = addChild(element, 'title', {
                unique: true,
                unshift: true
              });

              try {
                var xmlObject = blocks2Docbook.xmlConverter.parse("<title>" + attributes.caption.replace(/> </g, ">" + SPACE_CONSTANT + "<") + "</title>");

                if (xmlObject.$$.length > 0) {
                  xmlObject.$$[0].$$.forEach(function (child) {
                    addChild(titleElement, instance.normalizer.normalize(child));
                  });
                }
              } catch (error) {
                if (!error.message.includes('XML parsing error')) {
                  // eslint-disable-next-line no-console
                  console.error(error);
                }

                addChild(titleElement, {
                  type: 'text',
                  text: attributes.caption
                });
              }

              break;
            }

          case 'hasFixedLayout':
            // skipped, always false for now
            break;

          case 'className':
            element.attributes.role = attributes.className || undefined;
            break;

          case 'align':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'alignment', {
              unique: true
            }), 'attributes.horizontal', attributes.align);
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      var groupElement = addChild(element, 'tgroup');
      groupElement.attributes.cols = ((_head$ = head[0]) === null || _head$ === void 0 ? void 0 : (_head$$cells = _head$.cells) === null || _head$$cells === void 0 ? void 0 : _head$$cells.length) || ((_foot$ = foot[0]) === null || _foot$ === void 0 ? void 0 : (_foot$$cells = _foot$.cells) === null || _foot$$cells === void 0 ? void 0 : _foot$$cells.length) || ((_body$ = body[0]) === null || _body$ === void 0 ? void 0 : (_body$$cells = _body$.cells) === null || _body$$cells === void 0 ? void 0 : _body$$cells.length) || 0;

      if (groupElement.attributes.cols < 1) {
        throw new Error('tgroup must have at least single column');
      }

      formatRows(groupElement, head, 'thead');
      formatRows(groupElement, foot, 'tfoot');
      formatRows(groupElement, body, 'tbody');
      return element;
    }
  }));
};

var _excluded$4 = ["href", "rel", "linkClass", "linkTarget", "linkDestination"];

var Image = function Image(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var parentContext = blocks2Docbook.getParentContext();
      var linkAttributes = Object.keys(parentContext.link || {}).reduce(function (acc, attribute) {
        switch (attribute) {
          case 'url':
            acc.href = parentContext.link.url;
            break;

          case 'rel':
            acc.rel = parentContext.link.rel;
            break;

          case 'role':
            acc.linkClass = parentContext.link.role;
            break;

          case 'type':
            acc.linkTarget = parentContext.link.type;
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }

        return acc;
      }, {
        linkDestination: 'none'
      });
      var block = {
        name: 'core/image',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: Object.assign({}, linkAttributes)
      };
      element.$$.forEach(function (child) {
        switch (child.name) {
          case 'objectinfo':
            {
              var titleElement = child.$$[0];
              block.attributes.caption = blocks2Docbook.xmlConverter.format(Object.assign({}, titleElement, {
                $$: titleElement.$$.map(function (inlineChild) {
                  return instance.normalizer.denormalize(inlineChild);
                })
              }));
              break;
            }

          case 'imageobject':
            child.$$.forEach(function (imageChild) {
              switch (imageChild.name) {
                case 'objectinfo':
                  block.attributes.alt = imageChild.$$.find(function (item) {
                    return item.name === 'title';
                  }).$$[0].text;
                  break;

                case 'imagedata':
                  Object.keys(imageChild.attributes || {}).forEach(function (attribute) {
                    if (typeof imageChild.attributes[attribute] === 'undefined' || imageChild.attributes[attribute] === null) {
                      return;
                    }

                    switch (attribute) {
                      case 'align':
                        block.attributes.align = imageChild.attributes.align;
                        break;

                      case 'width':
                        block.attributes.width = +imageChild.attributes.width;
                        break;

                      case 'depth':
                        block.attributes.height = +imageChild.attributes.depth;
                        break;

                      case 'fileref':
                        block.attributes.url = imageChild.attributes.fileref;
                        break;

                      default:
                        // eslint-disable-next-line no-console
                        console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
                    }
                  });
                  break;

                default:
                  // eslint-disable-next-line no-console
                  console.warn(new Error("Element '" + imageChild.name + "' is not supported!"));
              }
            });
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Element '" + child.name + "' is not supported!"));
        }
      });
      return block;
    },
    format: function format(block) {
      var blockAttributes = block.attributes;

      var href = blockAttributes.href,
          rel = blockAttributes.rel,
          linkClass = blockAttributes.linkClass,
          linkTarget = blockAttributes.linkTarget,
          linkDestination = blockAttributes.linkDestination,
          attributes = _objectWithoutPropertiesLoose(blockAttributes, _excluded$4);

      var hasLink = !!href;
      var element = {
        name: hasLink ? 'inlinemediaobject' : 'mediaobject',
        type: 'element',
        attributes: {},
        $$: []
      };
      var imageObjectElement = addChild(element, 'imageobject', {
        unique: true
      });
      var imageDataElement = addChild(imageObjectElement, 'imagedata', {
        unique: true
      });
      Object.keys(attributes).forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'align':
            imageDataElement.attributes.align = attributes.align;
            break;

          case 'alt':
            {
              if (attributes.alt) {
                var objectInfoElement = addChild(imageObjectElement, 'objectinfo', {
                  unique: true,
                  unshift: true
                });
                var titleElement = addChild(objectInfoElement, 'title', {
                  unique: true
                });
                addChild(titleElement, {
                  type: 'text',
                  text: attributes.alt
                });
              }

              break;
            }

          case 'caption':
            {
              if (attributes.caption) {
                var objectInfo = addChild(element, 'objectinfo', {
                  unique: true,
                  unshift: true
                });

                var _titleElement = addChild(objectInfo, 'title', {
                  unique: true
                });

                try {
                  var xmlObject = blocks2Docbook.xmlConverter.parse("<title>" + attributes.caption.replace(/> </g, ">" + SPACE_CONSTANT + "<") + "</title>");

                  if (xmlObject.$$.length > 0) {
                    xmlObject.$$[0].$$.forEach(function (child) {
                      addChild(_titleElement, instance.normalizer.normalize(child));
                    });
                  }
                } catch (error) {
                  if (!error.message.includes('XML parsing error')) {
                    // eslint-disable-next-line no-console
                    console.error(error);
                  }

                  addChild(_titleElement, {
                    type: 'text',
                    text: attributes.caption
                  });
                }
              }

              break;
            }

          case 'width':
            imageDataElement.attributes.width = attributes.width || undefined;
            break;

          case 'height':
            imageDataElement.attributes.depth = attributes.height || undefined;
            break;

          case 'url':
            imageDataElement.attributes.fileref = attributes.url;
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });

      if (hasLink) {
        var linkBlockElement = {
          name: 'linkblock',
          type: 'element',
          attributes: {},
          $$: []
        };
        var linkElement = addChild(linkBlockElement, {
          name: 'ulink',
          type: 'element',
          attributes: {
            url: href,
            role: linkClass,
            type: linkTarget
          },
          $$: []
        });
        addChild(linkElement, element);

        if (rel) {
          addChild(linkElement, {
            name: blocks2Docbook.namespace + ":semantics",
            type: 'element',
            attributes: {
              rel: rel
            }
          });
        }

        return linkBlockElement;
      }

      return element;
    }
  }));
};

var _excluded$5 = ["id", "taxonomyname"],
    _excluded2$3 = ["namespace", "entity", "id", "isTaxonomy"];

var Embed = function Embed(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook,
      _ref$supportsVariatio = _ref.supportsVariation,
      supportsVariation = _ref$supportsVariatio === void 0 ? true : _ref$supportsVariatio;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };

  var parseEntityAttributes = function parseEntityAttributes(element) {
    var _element$attributes = element.attributes;
    _element$attributes = _element$attributes === void 0 ? {} : _element$attributes;
    var taxonomyname = _element$attributes.taxonomyname;

    var _element$name$split = element.name.split(':'),
        namespace = _element$name$split[0],
        entity = _element$name$split[1];

    if (!!taxonomyname && entity === 'taxon') {
      return {
        namespace: namespace,
        entity: Pluralize.singular(taxonomyname),
        isTaxonomy: true
      };
    }

    return {
      namespace: namespace,
      entity: entity,
      isTaxonomy: false
    };
  };

  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var _parseEntityAttribute = parseEntityAttributes(element),
          namespace = _parseEntityAttribute.namespace,
          entity = _parseEntityAttribute.entity,
          isTaxonomy = _parseEntityAttribute.isTaxonomy;

      var name = namespace + "/" + entity;
      var block = {
        name: name,
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {
          namespace: namespace,
          entity: Pluralize(entity),
          isTaxonomy: isTaxonomy
        }
      };
      var elementAttributes = element.attributes;

      var _ref2 = elementAttributes || {},
          id = _ref2.id,
          taxonomyname = _ref2.taxonomyname,
          attributes = _objectWithoutPropertiesLoose(_ref2, _excluded$5);

      if (!id) {
        throw new Error("Attribute id is required for block '" + name + "'");
      } else {
        block.attributes.id = id;
      }

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        }

        if (child.name === blocks2Docbook.namespace + ":semantics") {
          if (acc.semanticsElements) {
            throw new Error(blocks2Docbook.namespace + ":semantics element must be unique!");
          }

          Object.keys(child.attributes || {}).forEach(function (attribute) {
            switch (attribute) {
              case 'href':
                block.attributes.href = child.attributes.href;
                break;

              default:
                // eslint-disable-next-line no-console
                console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
            }
          });
          acc.semanticsElements = child;
        } else {
          acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements;

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          var _presentationElement$, _presentationElement$2;

          switch (presentationElement.name) {
            case 'alignment':
              block.attributes.align = (_presentationElement$ = presentationElement.attributes) === null || _presentationElement$ === void 0 ? void 0 : _presentationElement$.horizontal;
              break;

            case 'width':
              block.attributes.width = +((_presentationElement$2 = presentationElement.attributes) === null || _presentationElement$2 === void 0 ? void 0 : _presentationElement$2.value);
              break;

            case 'depth':
              {
                var _presentationElement$3;

                block.attributes.height = +((_presentationElement$3 = presentationElement.attributes) === null || _presentationElement$3 === void 0 ? void 0 : _presentationElement$3.value);
                break;
              }

            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      Object.keys(attributes).forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'variation':
            if (supportsVariation) {
              block.attributes.variation = {
                id: attributes.variation
              };
            }

            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      return block;
    },
    format: function format(block) {
      var blockAttributes = block.attributes;

      var _blockAttributes$name = blockAttributes.namespace,
          namespace = _blockAttributes$name === void 0 ? blocks2Docbook.namespace : _blockAttributes$name,
          entity = blockAttributes.entity,
          id = blockAttributes.id,
          isTaxonomy = blockAttributes.isTaxonomy,
          attributes = _objectWithoutPropertiesLoose(blockAttributes, _excluded2$3);

      if (!entity || !namespace) {
        throw new Error("Attributes entity and namespace are required for element <" + blocks2Docbook.namespace + ":embed/>");
      }

      var name = namespace + ":" + Pluralize.singular(isTaxonomy ? 'taxon' : entity);
      var element = {
        name: name,
        type: 'element',
        attributes: {},
        $$: []
      };

      if (!id) {
        return undefined;
      }

      if (isTaxonomy) {
        element.attributes.taxonomyname = entity;
      }

      element.attributes.id = id;
      var attributeKeys = Object.keys(attributes);
      var presentation;
      var semantics;
      attributeKeys.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'width':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'width', {
              unique: true
            }), 'attributes.value', attributes.width);
            set(addChild(presentation, 'width', {
              unique: true
            }), 'attributes.unit', 'px');
            break;

          case 'height':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'depth', {
              unique: true
            }), 'attributes.value', attributes.height);
            set(addChild(presentation, 'depth', {
              unique: true
            }), 'attributes.unit', 'px');
            break;

          case 'variation':
            if (supportsVariation) {
              element.attributes.variation = attributes.variation.id;
            }

            break;

          case 'align':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'alignment', {
              unique: true
            }), 'attributes.horizontal', attributes.align);
            break;

          case 'href':
            semantics = addChild(element, blocks2Docbook.namespace + ":semantics", {
              unique: true
            });
            set(semantics, 'attributes.href', attributes.href);
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      return element;
    }
  }));
};

var LinkBlock = function LinkBlock(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        } else {
          acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements,
          children = _element$$$$reduce.children;

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          switch (presentationElement.name) {
            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      var link = children[0];

      if (!link) {
        return null;
      }

      if (link.name !== 'ulink') {
        throw new Error("Child with name 'ulink' expected, got '" + (link.name || link.type) + "'");
      }

      var _parseChildren = parseChildren(link, blocks2Docbook.namespace, function (child, childType) {
        switch (childType) {
          case blocks2Docbook.namespace + ":semantics":
            Object.keys(child.attributes || {}).forEach(function (attribute) {
              switch (attribute) {
                case 'rel':
                  link.attributes.rel = child.attributes.rel;
                  break;

                default:
                  // eslint-disable-next-line no-console
                  console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
              }
            });
            break;

          case 'inlinemediaobject':
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Element '" + childType + "' is not supported!"));
        }
      }),
          linkChildren = _parseChildren.children;

      blocks2Docbook.setParentContext({
        link: link.attributes
      });
      var blocks = blocks2Docbook.parse({
        $$: linkChildren
      });
      blocks2Docbook.removeParentContext();
      return blocks;
    },
    format: function format(block) {
      throw new Error('hmn/linkblock does not support format');
    }
  }));
};

var MediaObject = function MediaObject(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var block;
      element.$$.forEach(function (child) {
        switch (child.name) {
          case 'videoobject':
            {
              var _videoData$attributes3;

              var videoData = child.$$.find(function (item) {
                return item.name === 'videodata';
              });

              if (!videoData) {
                // eslint-disable-next-line no-console
                console.log('videodata is required for element videoobject');
                break;
              }

              var providerNameSlug = Object.keys(blocks2Docbook.mediaUrlMatchers).find(function (name) {
                var _videoData$attributes;

                return blocks2Docbook.mediaUrlMatchers[name].test((_videoData$attributes = videoData.attributes) === null || _videoData$attributes === void 0 ? void 0 : _videoData$attributes.fileref);
              });

              if (!providerNameSlug) {
                var _videoData$attributes2;

                // eslint-disable-next-line no-console
                console.warn( // eslint-disable-next-line max-len
                "Provider not found for video '" + ((_videoData$attributes2 = videoData.attributes) === null || _videoData$attributes2 === void 0 ? void 0 : _videoData$attributes2.fileref) + "'. Maybe you are missing 'mediaUrlMatchers' param for your provider");
                break;
              }

              block = {
                name: 'core/embed',
                clientId: uuid(),
                innerBlocks: [],
                isValid: true,
                attributes: {
                  namespace: blocks2Docbook.namespace,
                  allowResponsive: true,
                  previewable: true,
                  providerNameSlug: providerNameSlug,
                  type: 'video',
                  url: (_videoData$attributes3 = videoData.attributes) === null || _videoData$attributes3 === void 0 ? void 0 : _videoData$attributes3.fileref
                }
              };
              break;
            }

          default:
            block = Image({
              blocks2Docbook: blocks2Docbook
            }).parse(element);
        }
      });
      return block;
    },
    format: function format(block) {
      var name = block.name,
          attributes = block.attributes;

      switch (name) {
        case 'core/embed':
          switch (attributes.type) {
            case 'video':
              {
                var mediaObject = createElement('mediaobject');
                var videoObject = addChild(mediaObject, 'videoobject');
                set(addChild(videoObject, 'videodata'), 'attributes.fileref', attributes.url);
                return mediaObject;
              }

            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Block '" + name + "' is not supported!"));
              return undefined;
          }

        default:
          return Image({
            blocks2Docbook: blocks2Docbook
          }).format(block);
      }
    }
  }));
};

var _excluded$6 = ["src"],
    _excluded2$4 = ["providerNameSlug", "url", "type"];

var Embed$1 = function Embed(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    }),
    providerNameSlugMappings: {
      internet: 'iframe'
    }
  };
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var _element$name$split = element.name.split(':'),
          namespace = _element$name$split[0],
          providerNameSlug = _element$name$split[1];

      var block = {
        name: 'core/embed',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {
          namespace: namespace,
          allowResponsive: true,
          previewable: true,
          providerNameSlug: providerNameSlug
        }
      };
      var elementAttributes = element.attributes;

      var src = elementAttributes.src,
          attributes = _objectWithoutPropertiesLoose(elementAttributes, _excluded$6);

      if (!src) {
        throw new Error("Attribute src is required for block core/embed");
      } else {
        block.attributes.url = src;
      }

      Object.keys(attributes).forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        } else {
          acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements,
          children = _element$$$$reduce.children;

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          var _presentationElement$, _presentationElement$2;

          switch (presentationElement.name) {
            case 'alignment':
              block.attributes.align = (_presentationElement$ = presentationElement.attributes) === null || _presentationElement$ === void 0 ? void 0 : _presentationElement$.horizontal;
              break;

            case 'depth':
              block.attributes.height = ((_presentationElement$2 = presentationElement.attributes) === null || _presentationElement$2 === void 0 ? void 0 : _presentationElement$2.value) || '';
              break;

            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      children.forEach(function (child) {
        switch (child.name) {
          case 'title':
            block.attributes.caption = blocks2Docbook.xmlConverter.format(Object.assign({}, child, {
              $$: child.$$.map(function (inlineChild) {
                return instance.normalizer.denormalize(inlineChild);
              })
            }));
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Element '" + child.name + "' is not supported!"));
        }
      });
      return block;
    },
    format: function format(block) {
      var blockAttributes = block.attributes;

      var originalProviderNameSlug = blockAttributes.providerNameSlug,
          url = blockAttributes.url,
          type = blockAttributes.type,
          attributes = _objectWithoutPropertiesLoose(blockAttributes, _excluded2$4);

      var namespace = attributes.namespace || blocks2Docbook.namespace;
      var providerNameSlug = instance.providerNameSlugMappings[originalProviderNameSlug] || originalProviderNameSlug || 'iframe';
      var name = namespace + ":" + providerNameSlug;
      var isMediaMatched = Object.keys(blocks2Docbook.mediaUrlMatchers).find(function (provider) {
        return blocks2Docbook.mediaUrlMatchers[provider].test(url);
      }); // if url is matched as media object then embed should be
      // transformed as mediaobject instead of embed

      if (isMediaMatched) {
        return MediaObject({
          blocks2Docbook: blocks2Docbook
        }).format(block);
      }

      if (!providerNameSlug || !namespace || !url) {
        return undefined;
      }

      var element = {
        name: name,
        type: 'element',
        attributes: {
          src: url
        },
        $$: []
      };
      var presentationAttributes = Object.keys(attributes);
      var presentation;
      presentationAttributes.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'caption':
            {
              if (attributes.caption) {
                var titleElement = addChild(element, 'title', {
                  unique: true,
                  unshift: true
                });

                try {
                  var xmlObject = blocks2Docbook.xmlConverter.parse("<title>" + attributes.caption.replace(/> </g, ">" + SPACE_CONSTANT + "<") + "</title>");

                  if (xmlObject.$$.length > 0) {
                    xmlObject.$$[0].$$.forEach(function (child) {
                      addChild(titleElement, instance.normalizer.normalize(child));
                    });
                  }
                } catch (error) {
                  if (!error.message.includes('XML parsing error')) {
                    // eslint-disable-next-line no-console
                    console.error(error);
                  }

                  addChild(titleElement, {
                    type: 'text',
                    text: attributes.caption
                  });
                }
              }

              break;
            }

          case 'align':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'alignment', {
              unique: true
            }), 'attributes.horizontal', attributes.align);
            break;

          case 'height':
            {
              if (attributes.height) {
                presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
                  unique: true
                });
                set(addChild(presentation, 'depth', {
                  unique: true
                }), 'attributes.value', attributes.height);
                set(addChild(presentation, 'depth', {
                  unique: true
                }), 'attributes.unit', 'px');
              }

              break;
            }

          case 'allowResponsive':
            break;

          case 'previewable':
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      return element;
    }
  }));
};

var _excluded$7 = ["level"];

var Heading = function Heading(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var block = {
        name: 'core/heading',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {
          level: {
            heading1: 1,
            heading2: 2,
            heading3: 3,
            heading4: 4,
            heading5: 5,
            heading6: 6,
            title: 2,
            subtitle: 3
          }[element.name]
        }
      };

      if (block.attributes.level < 1 || block.attributes.level > 6) {
        throw new Error('Valid level values are 1-6');
      }

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        } else {
          acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements,
          children = _element$$$$reduce.children;

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          var _presentationElement$;

          switch (presentationElement.name) {
            case 'alignment':
              block.attributes.textAlign = (_presentationElement$ = presentationElement.attributes) === null || _presentationElement$ === void 0 ? void 0 : _presentationElement$.horizontal;
              break;

            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      if (children !== null && children !== void 0 && children.some(function (child) {
        return child.type !== 'text';
      })) {
        var xmlString = blocks2Docbook.xmlConverter.format({
          $$: [{
            name: 'heading',
            type: 'element',
            $$: children.map(function (child) {
              return instance.normalizer.denormalize(child);
            })
          }]
        });
        block.attributes.content = xmlString.replace(/^<heading>/, '').replace(/<\/heading>$/, '');
      } else {
        var _children$find;

        block.attributes.content = ((_children$find = children.find(function (child) {
          return child.type === 'text';
        })) === null || _children$find === void 0 ? void 0 : _children$find.text) || '';
      }

      return block;
    },
    format: function format(block) {
      var blockAttributes = block.attributes;

      var level = blockAttributes.level,
          attributes = _objectWithoutPropertiesLoose(blockAttributes, _excluded$7);

      var element = {
        name: {
          1: 'heading1',
          2: 'heading2',
          3: 'heading3',
          4: 'heading4',
          5: 'heading5',
          6: 'heading6'
        }[level],
        type: 'element',
        attributes: {},
        $$: []
      };

      if (!element.name) {
        throw new Error("Unknown attribute level '" + level + "'");
      }

      var presentationAttributes = Object.keys(attributes);
      var presentation;
      presentationAttributes.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'content':
            try {
              if (attributes.content) {
                var xmlObject = blocks2Docbook.xmlConverter.parse("<heading>" + attributes.content.replace(/> </g, ">" + SPACE_CONSTANT + "<") + "</heading>");

                if (xmlObject.$$.length > 0) {
                  xmlObject.$$[0].$$.forEach(function (child) {
                    addChild(element, instance.normalizer.normalize(child));
                  });
                }
              }
            } catch (error) {
              if (!error.message.includes('XML parsing error')) {
                // eslint-disable-next-line no-console
                console.error(error);
              }

              addChild(element, {
                type: 'text',
                text: attributes.content
              });
            }

            break;

          case 'textAlign':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'alignment', {
              unique: true
            }), 'attributes.horizontal', attributes.textAlign);
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      return element;
    }
  }));
};

var Text = function Text(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {};
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var paraElement = {
        name: 'para',
        type: 'element',
        attributes: {},
        $$: [element]
      };
      return Paragraph({
        blocks2Docbook: blocks2Docbook
      }).parse(paraElement);
    }
  }));
};

var Quote = function Quote(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var block = {
        name: 'core/quote',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {}
      };

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        switch (child.name) {
          case blocks2Docbook.namespace + ":presentation":
            if (acc.presentationElements) {
              throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
            }

            acc.presentationElements = child;
            break;

          case 'attribution':
            if (acc.citation) {
              throw new Error("attribution element must be unique!");
            }

            acc.citation = child;
            break;

          case 'para':
            if (acc.value) {
              throw new Error("para element must be unique!");
            }

            acc.value = child;
            break;

          default:
            acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements,
          citation = _element$$$$reduce.citation,
          value = _element$$$$reduce.value;

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          var _presentationElement$;

          switch (presentationElement.name) {
            case 'alignment':
              block.attributes.align = (_presentationElement$ = presentationElement.attributes) === null || _presentationElement$ === void 0 ? void 0 : _presentationElement$.horizontal;
              break;

            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      if (citation) {
        var xmlString = blocks2Docbook.xmlConverter.format(Object.assign({}, citation, {
          $$: citation.$$.map(function (child) {
            return instance.normalizer.denormalize(child);
          })
        }));
        block.attributes.citation = xmlString;
      }

      if (value) {
        var _xmlString = blocks2Docbook.xmlConverter.format(Object.assign({}, value, {
          $$: value.$$.map(function (child) {
            return instance.normalizer.denormalize(child);
          })
        }));

        block.attributes.value = "<p>" + _xmlString + "</p>";
      }

      return block;
    },
    format: function format(block) {
      var attributes = block.attributes;
      var element = {
        name: 'blockquote',
        type: 'element',
        attributes: {},
        $$: []
      };
      var presentationAttributes = Object.keys(attributes);
      var presentation;
      presentationAttributes.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'citation':
            if (attributes.value) {
              var attributionElement = addChild(element, 'attribution', {
                unique: true,
                unshift: true
              });
              var xmlObject = blocks2Docbook.xmlConverter.parse("<attribution>" + attributes.citation.replace(/> </g, ">" + SPACE_CONSTANT + "<") + "</attribution>");

              if (xmlObject.$$.length > 0) {
                xmlObject.$$[0].$$.forEach(function (child) {
                  addChild(attributionElement, instance.normalizer.normalize(child));
                });
              }
            }

            break;

          case 'value':
            if (attributes.value) {
              var paraElement = addChild(element, 'para', {
                unique: true
              });

              var _xmlObject = blocks2Docbook.xmlConverter.parse(attributes.value.replace(/> </g, ">" + SPACE_CONSTANT + "<"));

              if (_xmlObject.$$.length > 0) {
                _xmlObject.$$[0].$$.forEach(function (child) {
                  addChild(paraElement, instance.normalizer.normalize(child));
                });
              }
            }

            break;

          case 'align':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'alignment', {
              unique: true
            }), 'attributes.horizontal', attributes.align);
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      return element;
    }
  }));
};

var _excluded$8 = ["width"];

var Column = function Column(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };

  var parseColumnWidth = function parseColumnWidth(value) {
    if (!isNumeric(value)) {
      return undefined;
    }

    return value + "%";
  };

  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var _blocks2Docbook$getPa = blocks2Docbook.getParentContext(),
          _blocks2Docbook$getPa2 = _blocks2Docbook$getPa.power,
          power = _blocks2Docbook$getPa2 === void 0 ? 1 : _blocks2Docbook$getPa2;

      var block = {
        name: 'core/column',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {
          width: parseColumnWidth(+element.attributes.size / power * 100 || undefined)
        }
      };

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        } else {
          acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements,
          children = _element$$$$reduce.children;

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          switch (presentationElement.name) {
            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      block.innerBlocks = blocks2Docbook.parse({
        $$: children
      });
      return block;
    },
    format: function format(block) {
      var blockAttributes = block.attributes;

      var width = blockAttributes.width,
          attributes = _objectWithoutPropertiesLoose(blockAttributes, _excluded$8);

      var _blocks2Docbook$getPa3 = blocks2Docbook.getParentContext(),
          _blocks2Docbook$getPa4 = _blocks2Docbook$getPa3.gcd,
          gcd = _blocks2Docbook$getPa4 === void 0 ? width : _blocks2Docbook$getPa4;

      var element = {
        name: 'column',
        type: 'element',
        attributes: {
          size: parseFloat(width) / gcd || 1
        },
        $$: []
      };
      var presentationAttributes = Object.keys(attributes);
      presentationAttributes.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      var children = blocks2Docbook.formatXmlObject(block.innerBlocks).$$[0].$$;

      if (children.length === 0) {
        addChild(element, 'para');
      } else {
        children.forEach(function (child) {
          addChild(element, child);
        });
      }

      return element;
    }
  }));
};

var Columns = function Columns(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var block = {
        name: 'core/columns',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {}
      };
      var power = 0;

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        } else if (child.name === 'column') {
          power += +child.attributes.size || 1;
          acc.children.push(child);
        } else {
          throw new Error("Element " + (child.name || child.type) + " is not a valid child!");
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements,
          children = _element$$$$reduce.children;

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          var _presentationElement$;

          switch (presentationElement.name) {
            case 'alignment':
              block.attributes.verticalAlignment = (_presentationElement$ = presentationElement.attributes) === null || _presentationElement$ === void 0 ? void 0 : _presentationElement$.vertical;
              break;

            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      blocks2Docbook.setParentContext(function (current) {
        return Object.assign({}, current, {
          power: power
        });
      });
      block.innerBlocks = children.map(function (child) {
        return Column({
          blocks2Docbook: blocks2Docbook
        }).parse(child);
      });
      blocks2Docbook.removeParentContext('power');
      return block;
    },
    format: function format(block) {
      var attributes = block.attributes;
      var element = {
        name: 'columns',
        type: 'element',
        attributes: {},
        $$: []
      };
      var presentationAttributes = Object.keys(attributes);
      var presentation;
      presentationAttributes.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          case 'verticalAlignment':
            presentation = addChild(element, blocks2Docbook.namespace + ":presentation", {
              unique: true
            });
            set(addChild(presentation, 'alignment', {
              unique: true
            }), 'attributes.vertical', attributes.verticalAlignment);
            break;

          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      var columnWidths = [];
      var innerBlocks = block.innerBlocks.map(function (innerBlock) {
        if (innerBlock.name !== 'core/column') {
          throw new Error("Element " + innerBlock.name + " is not a valid inner block!");
        }

        columnWidths.push(parseFloat(innerBlock.attributes.width) || 1);
        return innerBlock;
      });
      var gcd = gcdForNumbers(columnWidths);
      blocks2Docbook.setParentContext(function (current) {
        return Object.assign({}, current, {
          gcd: gcd
        });
      });
      innerBlocks.forEach(function (innerBlock) {
        addChild(element, Column({
          blocks2Docbook: blocks2Docbook
        }).format(innerBlock));
      });
      blocks2Docbook.removeParentContext('gcd');
      return element;
    }
  }));
};

var NextPage = function NextPage(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {
    normalizer: InlineNormalizer({
      blocks2Docbook: blocks2Docbook
    })
  };
  return Object.freeze(Object.assign({}, instance, {
    parse: function parse(element) {
      var block = {
        name: 'core/nextpage',
        clientId: uuid(),
        innerBlocks: [],
        isValid: true,
        attributes: {}
      };

      var _element$$$$reduce = element.$$.reduce(function (acc, child) {
        if (child.name === blocks2Docbook.namespace + ":presentation") {
          if (acc.presentationElements) {
            throw new Error(blocks2Docbook.namespace + ":presentation element must be unique!");
          }

          acc.presentationElements = child;
        } else {
          acc.children.push(child);
        }

        return acc;
      }, {
        children: []
      }),
          presentationElements = _element$$$$reduce.presentationElements;

      if (presentationElements !== null && presentationElements !== void 0 && presentationElements.$$) {
        presentationElements.$$.forEach(function (presentationElement) {
          switch (presentationElement.name) {
            default:
              // eslint-disable-next-line no-console
              console.warn(new Error("Attribute '" + presentationElement.name + "' is not supported!"));
          }
        });
      }

      return block;
    },
    format: function format(block) {
      var attributes = block.attributes;
      var element = {
        name: blocks2Docbook.namespace + ":pagebreak",
        type: 'element',
        attributes: {},
        $$: []
      };
      var presentationAttributes = Object.keys(attributes);
      presentationAttributes.forEach(function (attribute) {
        if (typeof attributes[attribute] === 'undefined' || attributes[attribute] === null) {
          return;
        }

        switch (attribute) {
          default:
            // eslint-disable-next-line no-console
            console.warn(new Error("Attribute '" + attribute + "' is not supported!"));
        }
      });
      return element;
    }
  }));
};

var parseMatch = /([a-z]{3,}):(image)|blockquote/i;

var ParaEmbedMigration = function ParaEmbedMigration(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      blocks2Docbook = _ref.blocks2Docbook;

  if (!blocks2Docbook) {
    throw new Error('blocks2Docbook is required!');
  }

  var instance = {};

  var parse = function parse(elements) {
    return elements.reduce(function (acc, element) {
      var _lastPara;

      var shouldApplyMigration = false;

      var _parseChildren = parseChildren(element, blocks2Docbook.namespace, function (child) {
        if (shouldApplyMigration) {
          return;
        }

        shouldApplyMigration = parseMatch.test(child.name || child.type);
      }),
          children = _parseChildren.children,
          presentationElement = _parseChildren.presentationElement,
          semanticElements = _parseChildren.semanticElements;

      if (!shouldApplyMigration) {
        return [element];
      }

      var result = [];
      var lastPara;
      children.forEach(function (child) {
        var nameOrType = child.name || child.type;

        if (parseMatch.test(nameOrType)) {
          if (lastPara) {
            result.push(lastPara);
            lastPara = undefined;
          }

          result.push(child);
          return;
        }

        if (!lastPara) {
          lastPara = createElement('para');

          if (presentationElement) {
            lastPara.push(presentationElement);
          }

          if (semanticElements) {
            lastPara.push(semanticElements);
          }
        }

        lastPara.$$.push(child);
      });

      if (((_lastPara = lastPara) === null || _lastPara === void 0 ? void 0 : _lastPara.$$.length) > 0) {
        result.push(lastPara);
      }

      return [].concat(acc, result);
    }, []);
  };

  return Object.freeze(Object.assign({}, instance, {
    parse: parse,
    format: function format(block) {
      return block;
    }
  }));
};

var isDev$1 = process.env.NODE_ENV !== 'production';

var Blocks2Docbook = function Blocks2Docbook(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$namespace = _ref.namespace,
      namespace = _ref$namespace === void 0 ? 'pio' : _ref$namespace,
      _ref$mediaUrlMatchers = _ref.mediaUrlMatchers,
      mediaUrlMatchers = _ref$mediaUrlMatchers === void 0 ? {} : _ref$mediaUrlMatchers,
      defaultOptions = _ref.options;

  if (!namespace) {
    throw new Error('namespace is required!');
  } // should not be forwarded to child instances
  // use instance for that


  var privateInstance = {
    parentContext: undefined,

    /**
     * Get parent context that is currently set
     *
     * @returns {Object}
     */
    getParentContext: function getParentContext() {
      // FYI this still exposes nested context values to be modified as non muttable
      return privateInstance.parentContext ? Object.assign({}, privateInstance.parentContext) : {};
    }
  };
  var instance = {
    namespace: namespace,
    mediaUrlMatchers: mediaUrlMatchers,
    // @EXPERIMENTAL
    // TODO expose migration to the outside users
    migrations: {
      para: [ParaEmbedMigration]
    },
    options: {
      noRoot: true
    },
    xmlConverter: XmlConverterService,
    getParentContext: privateInstance.getParentContext,

    /**
     * Set or update parent context
     *
     * Value is new context object or setter function that
     * can be used to extend current context value
     *
     * @param {Object|Function} setterOrContext
     */
    setParentContext: function setParentContext(setterOrContext) {
      privateInstance.parentContext = typeof setterOrContext === 'function' ? setterOrContext(privateInstance.getParentContext()) : setterOrContext;
    },

    /**
     * Clear all parent context that is currently set
     */
    removeParentContext: function removeParentContext(scope) {
      if (scope) {
        var current = privateInstance.getParentContext();
        delete current[scope];
        privateInstance.parentContext = current;
      } else {
        privateInstance.parentContext = undefined;
      }
    }
  };

  var flattenBlocksArray = function flattenBlocksArray(arrayOrBlock, acc) {
    var block = arrayOrBlock;

    if (!block) {
      return acc;
    }

    if (!Array.isArray(block)) {
      // this is to handle nested returns from element2Blocks
      // that can return single or many blocks
      block = [block];
    } else {
      block = block.flat();
    }

    return [].concat(acc, block.filter(Boolean));
  };
  /**
   * Convert XML element to Gutenberg block
   *
   * @param {Object} element
   * @param {boolean} [runMigrations=true] used to avoid infinite loops while applying migrations
   * @return {Object|Array}
   */


  var element2Blocks = function element2Blocks(element, runMigrations) {
    if (runMigrations === void 0) {
      runMigrations = true;
    }

    var migrationsToApply = instance.migrations[element.name || element.type];

    if (runMigrations && migrationsToApply) {
      var results = migrationsToApply.reduce(function (acc, createMigration) {
        return createMigration({
          blocks2Docbook: instance
        }).parse(acc);
      }, [element]);
      return results.map(function (item) {
        return element2Blocks(item, false);
      });
    }

    switch (element.name || element.type) {
      case 'text':
        return Text({
          blocks2Docbook: instance
        }).parse(element);

      case 'para':
        return Paragraph({
          blocks2Docbook: instance
        }).parse(element);

      case 'section':
        return element.$$.reduce(function (acc, child, index) {
          return flattenBlocksArray(element2Blocks(child, runMigrations), acc);
        }, []);

      case 'itemizedlist':
      case 'orderedlist':
        return List({
          blocks2Docbook: instance
        }).parse(element);

      case 'table':
        return Table({
          blocks2Docbook: instance
        }).parse(element);

      case 'mediaobject':
      case 'inlinemediaobject':
        return MediaObject({
          blocks2Docbook: instance
        }).parse(element);

      case namespace + ":article":
      case namespace + ":brand":
      case namespace + ":image":
      case namespace + ":ingredient":
      case namespace + ":product":
      case namespace + ":recipe":
        return Embed({
          blocks2Docbook: instance
        }).parse(element);

      case namespace + ":video":
      case namespace + ":file":
      case namespace + ":user":
      case namespace + ":taxon":
        return Embed({
          blocks2Docbook: instance,
          supportsVariation: false
        }).parse(element);

      case namespace + ":youtube":
      case namespace + ":instagram":
      case namespace + ":facebook":
      case namespace + ":twitter":
      case namespace + ":tiktok":
      case namespace + ":soundcloud":
      case namespace + ":vimeo":
      case namespace + ":iframe":
      case namespace + ":reddit":
      case namespace + ":telegram":
      case namespace + ":sofascore":
        return Embed$1({
          blocks2Docbook: instance
        }).parse(element);

      case 'title':
      case 'subtitle':
      case 'heading1':
      case 'heading2':
      case 'heading3':
      case 'heading4':
      case 'heading5':
      case 'heading6':
        return Heading({
          blocks2Docbook: instance
        }).parse(element);

      case 'blockquote':
        return Quote({
          blocks2Docbook: instance
        }).parse(element);

      case 'column':
        return Column({
          blocks2Docbook: instance
        }).parse(element);

      case 'columns':
        return Columns({
          blocks2Docbook: instance
        }).parse(element);

      case 'linkblock':
        return LinkBlock({
          blocks2Docbook: instance
        }).parse(element);

      case namespace + ":pagebreak":
        return NextPage({
          blocks2Docbook: instance
        }).parse(element);

      default:
        // eslint-disable-next-line no-console
        console.warn(new Error("Element '" + (element.name || element.type) + "' is not supported!"));
        return undefined;
    }
  };

  var block2Element = function block2Element(block) {
    switch (block.name) {
      case 'core/paragraph':
        return Paragraph({
          blocks2Docbook: instance
        }).format(block);

      case 'core/list':
        return List({
          blocks2Docbook: instance
        }).format(block);

      case 'core/table':
        return Table({
          blocks2Docbook: instance
        }).format(block);

      case 'core/image':
        return Image({
          blocks2Docbook: instance
        }).format(block);

      case namespace + "/article":
      case namespace + "/brand":
      case namespace + "/image":
      case namespace + "/ingredient":
      case namespace + "/product":
      case namespace + "/recipe":
      case namespace + "/video":
        return Embed({
          blocks2Docbook: instance
        }).format(block);

      case namespace + "/file":
      case namespace + "/user":
      case namespace + "/poll":
      case namespace + "/gallery":
        return Embed({
          blocks2Docbook: instance,
          supportsVariation: false
        }).format(block);

      case "core/embed":
        return Embed$1({
          blocks2Docbook: instance
        }).format(block);

      case 'core/heading':
        return Heading({
          blocks2Docbook: instance
        }).format(block);

      case 'core/quote':
        return Quote({
          blocks2Docbook: instance
        }).format(block);

      case 'core/column':
        return Column({
          blocks2Docbook: instance
        }).format(block);

      case 'core/columns':
        return Columns({
          blocks2Docbook: instance
        }).format(block);

      case 'core/nextpage':
        return NextPage({
          blocks2Docbook: instance
        }).format(block);

      default:
        // eslint-disable-next-line no-console
        console.warn(new Error("Block '" + block.name + "' is not supported!"));
        return undefined;
    }
  };

  instance.parse = function (xml, options) {
    if (options === void 0) {
      options = {};
    }

    var xmlObject = xml;

    if (typeof xml === 'string') {
      if (xml.length === 0) {
        return [];
      }

      xmlObject = instance.xmlConverter.parse(("<section>" + xml + "</section>").replace(/> </g, ">" + SPACE_CONSTANT + "<"), options);
    } else if (typeof xml !== 'object') {
      throw new Error('xml type is invalid, xml string or parsed object expected');
    }

    return xmlObject.$$.reduce(function (acc, element, index) {
      return flattenBlocksArray(element2Blocks(element), acc);
    }, []);
  };

  instance.formatXmlObject = function (blocks) {
    if (!Array.isArray(blocks)) {
      throw new Error('blocks type is invalid, array expected');
    }

    var xmlObject = {
      $$: [{
        name: 'section',
        type: 'element',
        $$: blocks.reduce(function (acc, block, index) {
          var element = block2Element(block);

          if (!element) {
            return acc;
          }

          return [].concat(acc, [element]);
        }, [])
      }]
    };
    return xmlObject;
  };

  instance.format = function (blocks, options) {
    if (options === void 0) {
      options = instance.options;
    }

    if (!Array.isArray(blocks)) {
      throw new Error('blocks type is invalid, array expected');
    }

    if (blocks.length === 0) {
      return null;
    }

    var xmlString = instance.xmlConverter.format(instance.formatXmlObject(blocks));

    if (options.noRoot) {
      return xmlString.replace(/^<section>/, '').replace(/<\/section>$/, '');
    }

    return xmlString;
  };

  return Object.freeze(Object.assign({}, instance));
};

if (isDev$1 && process.browser) {
  window.blocks2Docbook = Blocks2Docbook({
    namespace: process.env.DOCBOOK_NAMESPACE
  });
}

export default Blocks2Docbook;
export { XmlConverterService as XmlConverter };
